import React from 'react';
import { useFormikContext } from 'formik';
import { Alert, Box, Card, Flex, Text } from 'pounceV2';
import { useGetFilterFields } from 'Source/graphql/queries/getFilterFields.generated';
import CompoundInputFieldArrayWithJoinsV3, {
  CompoundInputFieldArrayMetaProps,
} from 'Components/forms/BaseDetectionForm/CompoundInputFieldArray/CompoundInputFieldArrayWithJoinsV3';
import { FiltersWithJoins } from 'Pages/Build/Detections/utils';
import { useTheme } from '@mui/material';
import FilterOperatorComboboxV3 from './FilterOperatorComboBox/FilterOperatorComboboxV3';
import FilterValueFieldV3 from './FilterValueField/FilterValueFieldV3';
import { addGroupNameToFilters } from './utils';
import { ExtractedFilterCondition, ExtractedFilterConditions } from './constants';
import FormikFilterFieldComboboxV3 from './FilterFieldComboBox/FormikFilterFieldComboboxV3';

type FilterValues = {
  inlineFiltersGUI?: FiltersWithJoins;
  filters?: ExtractedFilterConditions;
  logTypes: Array<string>;
};

interface DetectionFieldFiltersFormProps {
  disabled?: boolean;
}
const DetectionFieldFiltersFormV3: React.FC<DetectionFieldFiltersFormProps> = ({
  disabled = false,
}) => {
  const { values } = useFormikContext<FilterValues>();
  const logTypes = values?.logTypes;
  const hasLogTypes = React.useMemo(() => Boolean(logTypes?.length), [logTypes?.length]);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const {
    palette: { tokens, componentTokens },
  } = useTheme();

  const {
    loading: fieldsLoading,
    error: fieldsError,
    data,
  } = useGetFilterFields({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      input: {
        logTypes,
      },
    },
    skip: !hasLogTypes,
  });

  const groupedFilters = React.useMemo(() => addGroupNameToFilters(data), [data]);

  React.useEffect(() => {
    if (!fieldsLoading) {
      setHasLoaded(true);
    }
  }, [fieldsLoading]);

  return (
    <Card
      backgroundColor={tokens.background.neutral.inverse.raised[3]}
      borderWidth={0}
      display="flex"
      alignItems="flex-start"
      p={4}
      width="100%"
      overflow="visible"
    >
      {fieldsLoading ? (
        <Text color={tokens.content.text.accent[14][2]}>Loading...</Text>
      ) : (
        <>
          {fieldsError ? (
            <Box width="100%">
              <Alert variant="error" title="There was a problem loading the filters" />
            </Box>
          ) : (
            <Flex align="baseline">
              <Text mr={2} lineHeight="38px">
                Where
              </Text>
              <CompoundInputFieldArrayWithJoinsV3<ExtractedFilterCondition>
                name="inlineFiltersGUI"
                newItem={{
                  target: null,
                  operator: null,
                  value: null,
                }}
                disabled={disabled}
                allowGroups
                unitLabel="Filter"
                maximumNestingDepth={3}
              >
                {(
                  filter: ExtractedFilterCondition,
                  { namePrefix }: CompoundInputFieldArrayMetaProps,
                ) => (
                  <>
                    <FormikFilterFieldComboboxV3
                      menuTitle="Select a field from the list"
                      logTypes={logTypes}
                      allowJSONPath
                      autoFocus={hasLoaded}
                      name={`${namePrefix}.target`}
                      data-tid={`${namePrefix}.target`}
                      key={`${namePrefix}.target`}
                      disabled={!hasLogTypes || disabled}
                      color={componentTokens.tags.colors.condition.text.labelBlue}
                    />
                    <FilterOperatorComboboxV3
                      name={`${namePrefix}.operator`}
                      data-tid={`${namePrefix}.operator`}
                      key={`${namePrefix}.operator`}
                      disabled={!hasLogTypes || disabled}
                      currentOperator={filter?.operator}
                      fieldType={groupedFilters.find(f => f.target === filter?.target)?.type}
                      placeholder="Condition"
                      color={componentTokens.tags.colors.condition.text.labelGreen}
                    />
                    <FilterValueFieldV3
                      name={`${namePrefix}.value`}
                      data-tid={`${namePrefix}.value`}
                      key={`${namePrefix}.value`}
                      placeholder="Value"
                      disabled={!hasLogTypes || disabled}
                      currentOperator={filter?.operator}
                      fieldType={groupedFilters.find(f => f.target === filter?.target)?.type}
                      color={componentTokens.tags.colors.condition.text.labelBase}
                    />
                  </>
                )}
              </CompoundInputFieldArrayWithJoinsV3>
            </Flex>
          )}
        </>
      )}
    </Card>
  );
};

export default React.memo(DetectionFieldFiltersFormV3);
